import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import type { CronJob, CreateCronJobRequest, UpdateCronJobRequest } from '../../types/cron.js';
import * as projectApi from '../../services/project-api.js';
import * as teamApi from '../../services/team-api.js';
import type { Project } from '../../types/project.js';
import type { SkillSummary } from '../../types/team.js';
import type { ProviderInfo } from '../../types/agent.js';
import {
  type RoutineCategory,
  ROUTINE_CATEGORIES,
  parseMetaDescription,
  buildMetaDescription,
  buildPromptFromCategory,
  buildDisplayText,
} from '../../utils/cron-utils.js';
import type { CpCronNotificationSettings } from './cp-cron-notification-settings.js';
import './cp-cron-notification-settings.js';
import '../agents/cp-agent-selector.js';

type ScheduleType = 'daily' | 'weekly' | 'monthly' | 'custom';

const WEEKDAYS: { value: string; label: string }[] = [
  { value: '1', label: 'Montag' },
  { value: '2', label: 'Dienstag' },
  { value: '3', label: 'Mittwoch' },
  { value: '4', label: 'Donnerstag' },
  { value: '5', label: 'Freitag' },
  { value: '6', label: 'Samstag' },
  { value: '0', label: 'Sonntag' },
];

function parseCronToScheduleType(schedule: string): ScheduleType {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return 'custom';

  const [min, hour, dom, mon, dow] = parts;

  if (!/^\d+$/.test(min) || !/^\d+$/.test(hour)) return 'custom';

  if (dom === '*' && mon === '*' && dow === '*') return 'daily';
  if (dom === '*' && mon === '*' && dow !== '*') return 'weekly';
  if (mon === '*' && dow === '*' && dom !== '*') return 'monthly';
  return 'custom';
}

function parseCronTime(schedule: string): { hour: string; minute: string } {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length < 2) return { hour: '07', minute: '00' };
  return {
    minute: parts[0].padStart(2, '0'),
    hour: parts[1].padStart(2, '0'),
  };
}

function parseCronWeekday(schedule: string): string {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length < 5) return '1';
  return parts[4] === '*' ? '1' : parts[4];
}

function parseCronDayOfMonth(schedule: string): string {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length < 3) return '1';
  return parts[2] === '*' ? '1' : parts[2];
}

function buildCronExpression(type: ScheduleType, hour: string, minute: string, weekday: string, dayOfMonth: string, customExpr: string): string {
  switch (type) {
    case 'daily': return `${parseInt(minute, 10)} ${parseInt(hour, 10)} * * *`;
    case 'weekly': return `${parseInt(minute, 10)} ${parseInt(hour, 10)} * * ${weekday}`;
    case 'monthly': return `${parseInt(minute, 10)} ${parseInt(hour, 10)} ${parseInt(dayOfMonth, 10)} * *`;
    case 'custom': return customExpr.trim();
  }
}

function isValidCronExpression(expr: string): boolean {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  // Atom: * | N | N-N, optionally followed by /N (step). Comma-separated lists allowed.
  const atom = String.raw`(\*|\d{1,2}(-\d{1,2})?)(\/\d{1,2})?`;
  const field = new RegExp(`^${atom}(,${atom})*$`);
  return parts.every(part => field.test(part));
}

function formatNextExecution(schedule: string): string {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return '';

  const [minStr, hourStr, domStr, , dowStr] = parts;
  const min = parseInt(minStr, 10);
  const hour = parseInt(hourStr, 10);
  if (isNaN(min) || isNaN(hour)) return '';

  const time = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

  if (domStr === '*' && dowStr === '*') {
    return `Taeglich um ${time}`;
  }
  if (domStr === '*' && dowStr !== '*') {
    const days: Record<string, string> = { '0': 'Sonntag', '1': 'Montag', '2': 'Dienstag', '3': 'Mittwoch', '4': 'Donnerstag', '5': 'Freitag', '6': 'Samstag', '7': 'Sonntag' };
    const dayLabel = dowStr.split(',').map(d => days[d] ?? d).join(', ');
    return `Jeden ${dayLabel} um ${time}`;
  }
  if (dowStr === '*' && domStr !== '*') {
    return `Am ${domStr}. jeden Monats um ${time}`;
  }
  return schedule;
}

@customElement('cp-cron-job-form')
export class CpCronJobForm extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) open = false;
  @property({ type: Object }) job: CronJob | null = null;

  @state() private _name = '';
  @state() private _prompt = '';
  @state() private _description = '';
  @state() private _routineCategory: RoutineCategory = 'custom';
  @state() private _scheduleType: ScheduleType = 'daily';
  @state() private _hour = '07';
  @state() private _minute = '00';
  @state() private _weekday = '1';
  @state() private _dayOfMonth = '1';
  @state() private _customCron = '';
  @state() private _agentId: number | null = null;
  @state() private _projectId: number | null = null;
  @state() private _timeoutMinutes = 30;
  @state() private _model = '';
  @state() private _provider = '';
  @state() private _providers: ProviderInfo[] = [];
  @state() private _modelsForProvider: string[] = [];
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _projects: Project[] = [];

  // Notification settings ref
  private _notificationRef: Ref<CpCronNotificationSettings> = createRef();

  // Skill selection
  @state() private _availableSkills: SkillSummary[] = [];
  @state() private _selectedSkillIds: string[] = [];
  @state() private _skillDropdownOpen = false;

  // Category-specific fields
  @state() private _emailFilter = '';
  @state() private _emailCategories = '';
  @state() private _emailAction = 'summary';
  @state() private _reviewScope = 'all';
  @state() private _reviewFocus = '';
  @state() private _journalType = 'daily';
  @state() private _journalPromptHint = '';

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('click', this._onDocumentClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('click', this._onDocumentClick);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) {
      this._resetForm();
      void this._loadProjects();
      void this._loadSkills();
      void this._loadProviders();
    }
  }

  private async _loadProjects(): Promise<void> {
    try {
      this._projects = await projectApi.getProjects('active');
    } catch {
      this._projects = [];
    }
  }

  private async _loadSkills(): Promise<void> {
    try {
      this._availableSkills = await teamApi.getSkills();
    } catch {
      this._availableSkills = [];
    }
  }

  private async _loadProviders(): Promise<void> {
    try {
      const res = await fetch('/api/providers', { credentials: 'include' });
      if (res.ok) {
        this._providers = await res.json() as ProviderInfo[];
        if (this._provider && this._providers.some(p => p.id === this._provider)) {
          await this._loadModelsForProvider(this._provider);
        } else if (this._providers.length > 0 && !this._provider) {
          // Don't auto-select — keep empty = "Standard"
          this._modelsForProvider = [];
        }
      }
    } catch {
      this._providers = [];
    }
  }

  private async _loadModelsForProvider(providerId: string): Promise<void> {
    if (!providerId) {
      this._modelsForProvider = [];
      return;
    }
    try {
      const res = await fetch(`/api/providers/${encodeURIComponent(providerId)}/models`, { credentials: 'include' });
      if (res.ok) {
        this._modelsForProvider = await res.json() as string[];
      } else {
        this._modelsForProvider = [];
      }
    } catch {
      this._modelsForProvider = [];
    }
  }

  private _resetForm(): void {
    this._saving = false;
    this._error = '';
    this._skillDropdownOpen = false;

    // Reset category fields
    this._emailFilter = '';
    this._emailCategories = '';
    this._emailAction = 'summary';
    this._reviewScope = 'all';
    this._reviewFocus = '';
    this._journalType = 'daily';
    this._journalPromptHint = '';

    if (this.job) {
      this._name = this.job.name;
      this._prompt = this.job.prompt ?? this.job.handler;
      this._scheduleType = parseCronToScheduleType(this.job.schedule);
      const { hour, minute } = parseCronTime(this.job.schedule);
      this._hour = hour;
      this._minute = minute;
      this._weekday = parseCronWeekday(this.job.schedule);
      this._dayOfMonth = parseCronDayOfMonth(this.job.schedule);
      this._customCron = this._scheduleType === 'custom' ? this.job.schedule : '';
      this._agentId = this.job.agent_id;
      this._projectId = this.job.project_id;
      this._timeoutMinutes = this.job.timeout_minutes;
      this._selectedSkillIds = [...(this.job.skill_ids ?? [])];
      this._model = this.job.model ?? '';
      this._provider = this.job.provider ?? '';

      // Parse META:: from description
      const parsed = parseMetaDescription(this.job.description);
      if (parsed.meta) {
        this._routineCategory = parsed.meta.category;
        this._description = parsed.displayText;
        // Restore category-specific fields
        this._emailFilter = parsed.meta.emailFilter ?? '';
        this._emailCategories = parsed.meta.emailCategories ?? '';
        this._emailAction = parsed.meta.emailAction ?? 'summary';
        this._reviewScope = parsed.meta.reviewScope ?? 'all';
        this._reviewFocus = parsed.meta.reviewFocus ?? '';
        this._journalType = parsed.meta.journalType ?? 'daily';
        this._journalPromptHint = parsed.meta.journalPromptHint ?? '';
      } else {
        // Legacy job without META:: → treat as custom
        this._routineCategory = 'custom';
        this._description = this.job.description ?? '';
      }
    } else {
      this._name = '';
      this._prompt = '';
      this._description = '';
      this._routineCategory = 'custom';
      this._scheduleType = 'daily';
      this._hour = '07';
      this._minute = '00';
      this._weekday = '1';
      this._dayOfMonth = '1';
      this._customCron = '';
      this._agentId = null;
      this._projectId = null;
      this._timeoutMinutes = 30;
      this._selectedSkillIds = [];
      this._model = '';
      this._provider = '';
    }
  }

  private _close(): void {
    this.dispatchEvent(new CustomEvent('form-closed', { bubbles: true, composed: true }));
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      this._close();
    }
  };

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-cron-job-form__overlay')) {
      this._close();
    }
  }

  private _onDocumentClick = (e: MouseEvent): void => {
    if (!this._skillDropdownOpen) return;
    const target = e.target as HTMLElement;
    if (!target.closest('.cp-cron-job-form__skill-dropdown')) {
      this._skillDropdownOpen = false;
    }
  };

  private _toggleSkillDropdown(): void {
    if (this._availableSkills.length === 0) return;
    this._skillDropdownOpen = !this._skillDropdownOpen;
  }

  private _toggleSkill(skillId: string): void {
    if (this._selectedSkillIds.includes(skillId)) {
      this._selectedSkillIds = this._selectedSkillIds.filter(id => id !== skillId);
    } else {
      this._selectedSkillIds = [...this._selectedSkillIds, skillId];
    }
  }

  // ─── Input Handlers ────────────────────────────────────────────────

  private _onNameInput(e: Event): void {
    this._name = (e.target as HTMLInputElement).value;
  }

  private _onPromptInput(e: Event): void {
    this._prompt = (e.target as HTMLTextAreaElement).value;
  }

  private _onCategoryChange(e: Event): void {
    this._routineCategory = (e.target as HTMLSelectElement).value as RoutineCategory;
  }

  private _onScheduleTypeChange(e: Event): void {
    this._scheduleType = (e.target as HTMLSelectElement).value as ScheduleType;
  }

  private _onHourChange(e: Event): void {
    this._hour = (e.target as HTMLSelectElement).value;
  }

  private _onMinuteChange(e: Event): void {
    this._minute = (e.target as HTMLSelectElement).value;
  }

  private _onWeekdayChange(e: Event): void {
    this._weekday = (e.target as HTMLSelectElement).value;
  }

  private _onDayOfMonthChange(e: Event): void {
    this._dayOfMonth = (e.target as HTMLSelectElement).value;
  }

  private _onCustomCronInput(e: Event): void {
    this._customCron = (e.target as HTMLInputElement).value;
  }

  private _onAgentSelected(e: CustomEvent<{ agentId: number }>): void {
    this._agentId = e.detail.agentId;
    this._provider = '';
    this._model = '';
    this._modelsForProvider = [];
  }

  private _onAgentCleared(): void {
    this._agentId = null;
  }

  private async _onProviderChange(e: Event): Promise<void> {
    this._provider = (e.target as HTMLSelectElement).value;
    this._model = '';
    await this._loadModelsForProvider(this._provider);
  }

  private _onModelChange(e: Event): void {
    this._model = (e.target as HTMLSelectElement).value;
  }

  private _onProjectChange(e: Event): void {
    const val = (e.target as HTMLSelectElement).value;
    this._projectId = val ? parseInt(val, 10) : null;
  }

  private _onTimeoutChange(e: Event): void {
    this._timeoutMinutes = parseInt((e.target as HTMLInputElement).value, 10) || 30;
  }

  // Category-specific input handlers
  private _onEmailFilterInput(e: Event): void {
    this._emailFilter = (e.target as HTMLInputElement).value;
  }

  private _onEmailCategoriesInput(e: Event): void {
    this._emailCategories = (e.target as HTMLInputElement).value;
  }

  private _onEmailActionChange(e: Event): void {
    this._emailAction = (e.target as HTMLSelectElement).value;
  }

  private _onReviewScopeChange(e: Event): void {
    this._reviewScope = (e.target as HTMLSelectElement).value;
  }

  private _onReviewFocusInput(e: Event): void {
    this._reviewFocus = (e.target as HTMLInputElement).value;
  }

  private _onJournalTypeChange(e: Event): void {
    this._journalType = (e.target as HTMLSelectElement).value;
  }

  private _onJournalPromptHintInput(e: Event): void {
    this._journalPromptHint = (e.target as HTMLTextAreaElement).value;
  }

  // ─── Helpers ────────────────────────────────────────────────────────

  private _getCategoryFields(): Record<string, string> {
    switch (this._routineCategory) {
      case 'email':
        return {
          emailFilter: this._emailFilter,
          emailCategories: this._emailCategories,
          emailAction: this._emailAction,
        };
      case 'review':
        return {
          reviewScope: this._reviewScope,
          reviewFocus: this._reviewFocus,
        };
      case 'breakdown':
        return {};
      case 'journal':
        return {
          journalType: this._journalType,
          journalPromptHint: this._journalPromptHint,
        };
      case 'custom':
        return { prompt: this._prompt };
    }
  }

  // ─── Validation ────────────────────────────────────────────────────

  private get _cronExpression(): string {
    return buildCronExpression(this._scheduleType, this._hour, this._minute, this._weekday, this._dayOfMonth, this._customCron);
  }

  private get _isValid(): boolean {
    if (!this._name.trim()) return false;
    if (this._routineCategory === 'custom' && !this._prompt.trim()) return false;
    if (this._scheduleType === 'custom' && !isValidCronExpression(this._customCron)) return false;
    return true;
  }

  private get _cronError(): string {
    if (this._scheduleType === 'custom' && this._customCron.trim() && !isValidCronExpression(this._customCron)) {
      return 'Ungueltige Cron-Expression';
    }
    return '';
  }

  // ─── Save ──────────────────────────────────────────────────────────

  private async _onSave(): Promise<void> {
    if (!this._isValid || this._saving) return;

    this._saving = true;
    this._error = '';

    const schedule = this._cronExpression;
    const fields = this._getCategoryFields();

    // Build prompt from category (or use raw prompt for custom)
    const generatedPrompt = buildPromptFromCategory(this._routineCategory, fields);

    // Build META:: description
    let description: string | null;
    if (this._routineCategory === 'custom') {
      description = this._description.trim() || null;
    } else {
      const meta = { category: this._routineCategory, ...fields };
      const displayText = buildDisplayText(this._routineCategory, fields);
      description = buildMetaDescription(meta, displayText);
    }

    try {
      if (this.job) {
        const data: UpdateCronJobRequest = {
          name: this._name.trim(),
          schedule,
          handler: generatedPrompt,
          prompt: generatedPrompt,
          description,
          agent_id: this._agentId,
          project_id: this._projectId,
          timeout_minutes: this._timeoutMinutes,
          skill_ids: this._selectedSkillIds,
          model: this._model || null,
          provider: this._provider || null,
        };
        // Save notification settings if component is mounted
        if (this._notificationRef.value) {
          await this._notificationRef.value.save();
        }
        this.dispatchEvent(new CustomEvent('job-save', {
          detail: { mode: 'edit', jobId: this.job.id, data },
          bubbles: true,
          composed: true,
        }));
      } else {
        const data: CreateCronJobRequest = {
          name: this._name.trim(),
          schedule,
          handler: generatedPrompt,
          prompt: generatedPrompt,
          description,
          agent_id: this._agentId,
          project_id: this._projectId,
          timeout_minutes: this._timeoutMinutes,
          skill_ids: this._selectedSkillIds,
          model: this._model || null,
          provider: this._provider || null,
        };
        this.dispatchEvent(new CustomEvent('job-save', {
          detail: { mode: 'create', data },
          bubbles: true,
          composed: true,
        }));
      }
      this._close();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Speichern fehlgeschlagen';
    } finally {
      this._saving = false;
    }
  }

  // ─── Render ────────────────────────────────────────────────────────

  render() {
    if (!this.open) return nothing;

    const isEdit = !!this.job;
    const heading = isEdit ? 'Routine bearbeiten' : 'Neue Routine';
    const preview = formatNextExecution(this._cronExpression);

    return html`
      <div class="cp-cron-job-form__overlay" @click=${this._onBackdropClick}>
        <div class="cp-cron-job-form__dialog">
          <div class="cp-cron-job-form__header">
            <h2 class="cp-cron-job-form__heading">${heading}</h2>
            <button class="cp-cron-job-form__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-cron-job-form__body">
            ${this._renderNameField()}
            ${this._renderCategoryField()}
            ${this._renderCategorySpecificFields()}
            ${this._renderSkillsField()}
            ${this._renderAgentField()}
            ${this._renderScheduleSection()}
            ${preview ? html`
              <div class="cp-cron-job-form__preview">
                <span class="cp-cron-job-form__preview-label">Vorschau:</span>
                <span class="cp-cron-job-form__preview-text">${preview}</span>
              </div>
            ` : nothing}
            ${this._renderProjectField()}
            ${this._renderTimeoutField()}
            ${this._renderProviderModelFields()}
            ${this._renderNotificationSettings()}

            ${this._error ? html`
              <div class="cp-cron-job-form__error">${this._error}</div>
            ` : nothing}
          </div>

          <div class="cp-cron-job-form__footer">
            <button class="cp-cron-job-form__btn cp-cron-job-form__btn--cancel" @click=${this._close}>Abbrechen</button>
            <button
              class="cp-cron-job-form__btn cp-cron-job-form__btn--save"
              ?disabled=${!this._isValid || this._saving}
              @click=${this._onSave}
            >${this._saving ? 'Speichern...' : 'Speichern'}</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderNameField() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Name</label>
        <input
          class="cp-cron-job-form__input"
          type="text"
          placeholder="z.B. Email Triage"
          .value=${this._name}
          @input=${this._onNameInput}
        />
      </div>
    `;
  }

  private _renderCategoryField() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Typ</label>
        <select class="cp-cron-job-form__select" .value=${this._routineCategory} @change=${this._onCategoryChange}>
          ${ROUTINE_CATEGORIES.map(cat => html`
            <option value=${cat.value} ?selected=${this._routineCategory === cat.value}>${cat.label}</option>
          `)}
        </select>
        <span class="cp-cron-job-form__hint">
          ${ROUTINE_CATEGORIES.find(c => c.value === this._routineCategory)?.description ?? ''}
        </span>
      </div>
    `;
  }

  private _renderCategorySpecificFields() {
    switch (this._routineCategory) {
      case 'email':
        return this._renderEmailFields();
      case 'review':
        return this._renderReviewFields();
      case 'breakdown':
        return this._renderBreakdownFields();
      case 'journal':
        return this._renderJournalFields();
      case 'custom':
        return this._renderCustomFields();
    }
  }

  private _renderEmailFields() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Filter-Kriterien</label>
        <input
          class="cp-cron-job-form__input"
          type="text"
          placeholder="z.B. in:inbox is:unread"
          .value=${this._emailFilter}
          @input=${this._onEmailFilterInput}
        />
      </div>
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Kategorien <span class="cp-cron-job-form__optional">(kommagetrennt)</span></label>
        <input
          class="cp-cron-job-form__input"
          type="text"
          placeholder="z.B. Dringend, Review, Archiv"
          .value=${this._emailCategories}
          @input=${this._onEmailCategoriesInput}
        />
      </div>
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Aktion</label>
        <select class="cp-cron-job-form__select" .value=${this._emailAction} @change=${this._onEmailActionChange}>
          <option value="summary" ?selected=${this._emailAction === 'summary'}>Nur zusammenfassen</option>
          <option value="todos" ?selected=${this._emailAction === 'todos'}>To-Dos erstellen</option>
          <option value="both" ?selected=${this._emailAction === 'both'}>Beides</option>
        </select>
      </div>
    `;
  }

  private _renderReviewFields() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Scope</label>
        <select class="cp-cron-job-form__select" .value=${this._reviewScope} @change=${this._onReviewScopeChange}>
          <option value="prs" ?selected=${this._reviewScope === 'prs'}>Offene PRs</option>
          <option value="commits" ?selected=${this._reviewScope === 'commits'}>Letzte Commits</option>
          <option value="issues" ?selected=${this._reviewScope === 'issues'}>Issues</option>
          <option value="all" ?selected=${this._reviewScope === 'all'}>Alles</option>
        </select>
      </div>
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Fokus <span class="cp-cron-job-form__optional">(optional)</span></label>
        <input
          class="cp-cron-job-form__input"
          type="text"
          placeholder="z.B. Security, Performance"
          .value=${this._reviewFocus}
          @input=${this._onReviewFocusInput}
        />
      </div>
    `;
  }

  private _renderBreakdownFields() {
    return html`
      <div class="cp-cron-job-form__field">
        <span class="cp-cron-job-form__hint">
          Verwendet die bestehende Breakdown-Logik fuer deine Ziele. Keine zusaetzliche Konfiguration noetig.
        </span>
      </div>
    `;
  }

  private _renderJournalFields() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Typ</label>
        <select class="cp-cron-job-form__select" .value=${this._journalType} @change=${this._onJournalTypeChange}>
          <option value="daily" ?selected=${this._journalType === 'daily'}>Tagesreflexion</option>
          <option value="weekly" ?selected=${this._journalType === 'weekly'}>Wochenreview</option>
          <option value="monthly" ?selected=${this._journalType === 'monthly'}>Monatsreview</option>
          <option value="free" ?selected=${this._journalType === 'free'}>Freie Reflexion</option>
        </select>
      </div>
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Kontext / Leitfragen <span class="cp-cron-job-form__optional">(optional)</span></label>
        <textarea
          class="cp-cron-job-form__textarea"
          rows="3"
          placeholder="Optionale Leitfragen oder Kontext fuer die Reflexion"
          .value=${this._journalPromptHint}
          @input=${this._onJournalPromptHintInput}
        ></textarea>
      </div>
    `;
  }

  private _renderCustomFields() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Prompt / Befehl</label>
        <textarea
          class="cp-cron-job-form__textarea"
          rows="4"
          placeholder="Schreibe einen Prompt, der an den AI-Agenten gesendet wird"
          .value=${this._prompt}
          @input=${this._onPromptInput}
        ></textarea>
      </div>
    `;
  }

  private _renderSkillsField() {
    const hasSkills = this._availableSkills.length > 0;
    const selectedCount = this._selectedSkillIds.length;
    const buttonLabel = !hasSkills
      ? 'Keine Skills verfuegbar'
      : selectedCount === 0
        ? 'Keine Skills'
        : `${selectedCount} Skill${selectedCount > 1 ? 's' : ''} ausgewaehlt`;

    return html`
      <div class="cp-cron-job-form__field cp-cron-job-form__skill-dropdown">
        <label class="cp-cron-job-form__label">Skills <span class="cp-cron-job-form__optional">(optional)</span></label>
        <button
          type="button"
          class="cp-cron-job-form__skill-toggle"
          ?disabled=${!hasSkills}
          @click=${this._toggleSkillDropdown}
        >
          <span>${buttonLabel}</span>
          <span class="cp-cron-job-form__skill-chevron ${this._skillDropdownOpen ? 'cp-cron-job-form__skill-chevron--open' : ''}">\u25BE</span>
        </button>
        ${this._skillDropdownOpen ? html`
          <div class="cp-cron-job-form__skill-list">
            ${this._availableSkills.map(skill => {
              const checked = this._selectedSkillIds.includes(skill.id);
              return html`
                <label class="cp-cron-job-form__skill-item">
                  <input
                    type="checkbox"
                    .checked=${checked}
                    @change=${() => this._toggleSkill(skill.id)}
                  />
                  <div class="cp-cron-job-form__skill-info">
                    <span class="cp-cron-job-form__skill-name">${skill.name}</span>
                    <span class="cp-cron-job-form__skill-desc">${skill.description}</span>
                  </div>
                </label>
              `;
            })}
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderAgentField() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Agent <span class="cp-cron-job-form__optional">(optional)</span></label>
        <div class="cp-cron-job-form__agent-row">
          <cp-agent-selector
            class="cp-cron-job-form__agent-selector"
            .value=${this._agentId}
            @agent-selected=${this._onAgentSelected}
          ></cp-agent-selector>
          ${this._agentId !== null ? html`
            <button
              type="button"
              class="cp-cron-job-form__agent-clear"
              @click=${this._onAgentCleared}
              title="Agent entfernen"
            >&times;</button>
          ` : nothing}
        </div>
        <span class="cp-cron-job-form__hint">
          ${this._agentId !== null
            ? 'Agent-Persoenlichkeit aktiv. Provider/Modell vom Agenten uebernommen.'
            : 'Ohne Agent: System-Standard wird verwendet.'}
        </span>
      </div>
    `;
  }

  private _renderScheduleSection() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Schedule</label>
        <select class="cp-cron-job-form__select" .value=${this._scheduleType} @change=${this._onScheduleTypeChange}>
          <option value="daily" ?selected=${this._scheduleType === 'daily'}>Taeglich</option>
          <option value="weekly" ?selected=${this._scheduleType === 'weekly'}>Woechentlich</option>
          <option value="monthly" ?selected=${this._scheduleType === 'monthly'}>Monatlich</option>
          <option value="custom" ?selected=${this._scheduleType === 'custom'}>Custom</option>
        </select>
      </div>

      ${this._scheduleType === 'custom' ? this._renderCustomCron() : this._renderTimeFields()}
    `;
  }

  private _renderTimeFields() {
    return html`
      <div class="cp-cron-job-form__row">
        <div class="cp-cron-job-form__field cp-cron-job-form__field--half">
          <label class="cp-cron-job-form__label">Uhrzeit</label>
          <div class="cp-cron-job-form__time-row">
            <select class="cp-cron-job-form__select cp-cron-job-form__select--time" .value=${this._hour} @change=${this._onHourChange}>
              ${Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => html`
                <option value=${h} ?selected=${this._hour === h}>${h}</option>
              `)}
            </select>
            <span class="cp-cron-job-form__time-sep">:</span>
            <select class="cp-cron-job-form__select cp-cron-job-form__select--time" .value=${this._minute} @change=${this._onMinuteChange}>
              ${['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(m => html`
                <option value=${m} ?selected=${this._minute === m}>${m}</option>
              `)}
            </select>
          </div>
        </div>

        ${this._scheduleType === 'weekly' ? html`
          <div class="cp-cron-job-form__field cp-cron-job-form__field--half">
            <label class="cp-cron-job-form__label">Wochentag</label>
            <select class="cp-cron-job-form__select" .value=${this._weekday} @change=${this._onWeekdayChange}>
              ${WEEKDAYS.map(d => html`
                <option value=${d.value} ?selected=${this._weekday === d.value}>${d.label}</option>
              `)}
            </select>
          </div>
        ` : nothing}

        ${this._scheduleType === 'monthly' ? html`
          <div class="cp-cron-job-form__field cp-cron-job-form__field--half">
            <label class="cp-cron-job-form__label">Tag des Monats</label>
            <select class="cp-cron-job-form__select" .value=${this._dayOfMonth} @change=${this._onDayOfMonthChange}>
              ${Array.from({ length: 28 }, (_, i) => String(i + 1)).map(d => html`
                <option value=${d} ?selected=${this._dayOfMonth === d}>${d}.</option>
              `)}
            </select>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderCustomCron() {
    const cronErr = this._cronError;
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Cron-Expression</label>
        <input
          class="cp-cron-job-form__input cp-cron-job-form__input--mono ${cronErr ? 'cp-cron-job-form__input--error' : ''}"
          type="text"
          placeholder="z.B. */15 * * * *"
          .value=${this._customCron}
          @input=${this._onCustomCronInput}
        />
        ${cronErr ? html`
          <span class="cp-cron-job-form__field-error">${cronErr}</span>
        ` : nothing}
      </div>
    `;
  }

  private _renderProjectField() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Projekt <span class="cp-cron-job-form__optional">(optional)</span></label>
        <select class="cp-cron-job-form__select" @change=${this._onProjectChange}>
          <option value="" ?selected=${!this._projectId}>Kein Projekt</option>
          ${this._projects.map(p => html`
            <option value=${String(p.id)} ?selected=${this._projectId === p.id}>${p.name}</option>
          `)}
        </select>
      </div>
    `;
  }

  private _renderTimeoutField() {
    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Timeout <span class="cp-cron-job-form__optional">(Minuten)</span></label>
        <input
          class="cp-cron-job-form__input cp-cron-job-form__input--narrow"
          type="number"
          min="1"
          max="120"
          .value=${String(this._timeoutMinutes)}
          @input=${this._onTimeoutChange}
        />
      </div>
    `;
  }

  private _renderProviderModelFields() {
    const agentActive = this._agentId !== null;

    if (this._providers.length === 0) return nothing;

    return html`
      <div class="cp-cron-job-form__field">
        <label class="cp-cron-job-form__label">Provider <span class="cp-cron-job-form__optional">(optional)</span></label>
        <select
          class="cp-cron-job-form__select"
          .value=${this._provider}
          ?disabled=${agentActive}
          @change=${(e: Event) => void this._onProviderChange(e)}
        >
          <option value="" ?selected=${!this._provider}>Standard</option>
          ${this._providers.map(p => html`
            <option value=${p.id} ?selected=${this._provider === p.id}>${p.displayName}</option>
          `)}
        </select>
        ${agentActive ? html`
          <span class="cp-cron-job-form__hint">Vom Agenten uebernommen. Agent entfernen zum Ueberschreiben.</span>
        ` : nothing}
      </div>

      ${this._provider && this._modelsForProvider.length > 0 ? html`
        <div class="cp-cron-job-form__field">
          <label class="cp-cron-job-form__label">Modell <span class="cp-cron-job-form__optional">(optional)</span></label>
          <select
            class="cp-cron-job-form__select"
            .value=${this._model}
            ?disabled=${agentActive}
            @change=${(e: Event) => this._onModelChange(e)}
          >
            <option value="" ?selected=${!this._model}>Standard</option>
            ${this._modelsForProvider.map(m => html`
              <option value=${m} ?selected=${this._model === m}>${m}</option>
            `)}
          </select>
        </div>
      ` : nothing}
    `;
  }

  private _renderNotificationSettings() {
    const isEdit = !!this.job;
    if (!isEdit) return nothing;

    return html`
      <cp-cron-notification-settings
        ${ref(this._notificationRef)}
        .jobId=${this.job!.id}
      ></cp-cron-notification-settings>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-cron-job-form': CpCronJobForm;
  }
}
